<template>
  <div>
    <v-row
      dense
      class="mb-2"
    >
      <v-col
        cols="12"
        sm="9"
      >
        <v-text-field
          v-model="search"
          label="Buscar (apelido ou item de dicionário)"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          clearable
          hide-details
          @keyup.enter="reload(true)"
          @click:clear="onClear"
        />
      </v-col>
      <v-col
        cols="12"
        sm="3"
        class="d-flex align-center justify-end"
      >
        <v-btn
          color="secondary"
          small
          block
          @click="openCreate"
        >
          <v-icon
            left
            small
          >
            mdi-plus
          </v-icon>
          Novo apelido
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="tableHeaders"
      :items="items"
      :server-items-length="total"
      :options.sync="options"
      :loading="loading"
      :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      item-key="id"
      dense
    >
      <template #item.actions="{ item }">
        <v-btn
          icon
          small
          @click="openEdit(item)"
        >
          <v-icon small>
            mdi-pencil
          </v-icon>
        </v-btn>
        <v-btn
          icon
          small
          color="error"
          @click="remove(item)"
        >
          <v-icon small>
            mdi-delete
          </v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog
      v-model="dialog.show"
      max-width="560"
      persistent
    >
      <v-card>
        <v-card-title>
          {{ dialog.mode === 'create' ? `Novo apelido de ${parentLabel}` : `Editar apelido de ${parentLabel}` }}
        </v-card-title>
        <v-card-text>
          <v-autocomplete
            v-if="dialog.mode === 'create'"
            v-model="dialog.form.parentId"
            :items="parentOptions"
            :loading="parentLoading"
            :search-input.sync="parentSearch"
            :item-text="parentItemText"
            item-value="id"
            :label="`${parentLabel} (busque pelo código ou descrição)`"
            outlined
            dense
            no-filter
            hide-no-data
            clearable
          />
          <v-text-field
            v-else
            :value="dialog.parentLabelText"
            :label="parentLabel"
            outlined
            dense
            disabled
          />
          <v-text-field
            v-model="dialog.form.apelido"
            label="Apelido"
            outlined
            dense
            class="mt-2"
            maxlength="200"
          />
          <v-alert
            v-if="dialog.error"
            type="error"
            dense
            outlined
            border="left"
          >
            {{ dialog.error }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="dialog.saving"
            @click="dialog.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="secondary"
            :loading="dialog.saving"
            @click="submit"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
  import { $k } from '@/lib/kraken'

  export default {
    name: 'ApelidosTab',

    props: {
      // 'cbhpm_apelidos' or 'especialidades_apelidos'
      sdkPath: { type: String, required: true },
      // 'cbhpm' or 'especialidades' — used for parent autocomplete & dropdown
      parentSdkPath: { type: String, required: true },
      // 'id_dicionario_cbhpm' | 'id_dicionario_especialidades'
      parentIdField: { type: String, required: true },
      // human label for parent (e.g. 'Procedimento CBHPM' / 'Especialidade')
      parentLabel: { type: String, required: true },
      // headers for the apelido table (excluding actions, which is added)
      tableHeaders: { type: Array, required: true },
      // function (parentItem) => string used in the autocomplete & disabled field
      parentDisplay: { type: Function, required: true },
      active: { type: Boolean, default: false },
    },

    data: () => ({
      search: '',
      items: [],
      total: 0,
      options: { page: 1, itemsPerPage: 25 },
      loading: false,
      hasLoaded: false,
      snackbar: { show: false, color: 'success', text: '' },
      parentOptions: [],
      parentLoading: false,
      parentSearch: '',
      parentSearchTimer: null,
      selectedParent: null,
      dialog: {
        show: false,
        mode: 'create',
        saving: false,
        error: '',
        editingId: null,
        parentLabelText: '',
        form: { parentId: null, apelido: '' },
      },
    }),

    computed: {
      sdkNode () {
        // eslint-disable-next-line camelcase
        const root = $k?.manage?.clinic_one?.dictionaries
        if (!root) return null
        return root[this.sdkPath] || null
      },
      parentSdkNode () {
        // eslint-disable-next-line camelcase
        const root = $k?.manage?.clinic_one?.dictionaries
        if (!root) return null
        return root[this.parentSdkPath] || null
      },
    },

    watch: {
      options: {
        deep: true,
        handler () {
          if (this.active || this.hasLoaded) this.reload()
        },
      },
      active (val) {
        if (val && !this.hasLoaded) this.reload(true)
      },
      parentSearch (v) {
        // When the user clicks a suggestion Vuetify sets the search text to the
        // selected item's display string. Skip the refetch in that case so the
        // selected option is not wiped out by the next search results.
        if (this.selectedParent && v === this.parentDisplay(this.selectedParent)) return
        if (this.parentSearchTimer) clearTimeout(this.parentSearchTimer)
        this.parentSearchTimer = setTimeout(() => this.fetchParentOptions(v), 250)
      },
      'dialog.form.parentId' (id) {
        if (id == null) {
          this.selectedParent = null
          return
        }
        const found = this.parentOptions.find(o => o.id === id)
        if (found) this.selectedParent = found
      },
    },

    mounted () {
      if (this.active) this.reload(true)
    },

    methods: {
      parentItemText (item) {
        return this.parentDisplay(item)
      },
      onClear () {
        this.search = ''
        this.reload(true)
      },
      msgFromError (err, fallback) {
        return err?.response?.data?.messages?.error?.[0] ||
          err?.response?.data?.messages?.[0]?.text ||
          err?.message ||
          fallback
      },
      async reload (resetPage = false) {
        if (!this.sdkNode?.get) {
          this.snackbar = { show: true, color: 'error', text: `SDK ausente para "${this.sdkPath}". Reinstale @kraken/manage.` }
          return
        }
        if (resetPage && this.options.page !== 1) {
          this.options = { ...this.options, page: 1 }
          return
        }
        this.loading = true
        try {
          const skip = (this.options.page - 1) * this.options.itemsPerPage
          const payload = { skip, limit: this.options.itemsPerPage }
          if (this.search) payload.q = this.search
          const res = await this.sdkNode.get(payload)
          this.items = res.results || []
          this.total = res.total || 0
          this.hasLoaded = true
        } catch (err) {
          console.error(`[${this.sdkPath}.get]`, err)
          this.snackbar = { show: true, color: 'error', text: this.msgFromError(err, 'Falha ao carregar apelidos.') }
        } finally {
          this.loading = false
        }
      },
      async fetchParentOptions (q) {
        if (!this.parentSdkNode?.get) return
        this.parentLoading = true
        try {
          const payload = { skip: 0, limit: 25 }
          if (q && q.trim()) payload.q = q.trim()
          const res = await this.parentSdkNode.get(payload)
          const results = res.results || []
          // Make sure the currently selected parent stays in the options list,
          // otherwise v-autocomplete cannot render its label and clears it.
          if (this.selectedParent && !results.some(r => r.id === this.selectedParent.id)) {
            results.unshift(this.selectedParent)
          }
          this.parentOptions = results
        } catch (err) {
          console.error(`[${this.parentSdkPath}.get]`, err)
        } finally {
          this.parentLoading = false
        }
      },
      openCreate () {
        this.dialog = {
          show: true,
          mode: 'create',
          saving: false,
          error: '',
          editingId: null,
          parentLabelText: '',
          form: { parentId: null, apelido: '' },
        }
        this.selectedParent = null
        this.parentOptions = []
        this.parentSearch = ''
        this.fetchParentOptions('')
      },
      openEdit (item) {
        const parentLabelText = this.itemParentLabel(item)
        this.dialog = {
          show: true,
          mode: 'edit',
          saving: false,
          error: '',
          editingId: item.id,
          parentLabelText,
          form: { parentId: item[this.parentIdField], apelido: item.apelido },
        }
      },
      itemParentLabel (item) {
        // Build a label using fields present on the apelido row (cbhpm_codigo/descricao or especialidade_*)
        if (this.parentSdkPath === 'cbhpm') {
          return [item.cbhpm_codigo, item.cbhpm_descricao].filter(Boolean).join(' — ')
        }
        if (this.parentSdkPath === 'especialidades') {
          return [item.especialidade_cbo2002, item.especialidade_descricao].filter(Boolean).join(' — ')
        }
        return ''
      },
      async submit () {
        const { mode, form, editingId } = this.dialog
        if (!form.apelido?.trim()) {
          this.dialog.error = 'Apelido é obrigatório.'
          return
        }
        if (mode === 'create' && !form.parentId) {
          this.dialog.error = `Selecione um(a) ${this.parentLabel.toLowerCase()}.`
          return
        }
        this.dialog.saving = true
        this.dialog.error = ''
        try {
          if (mode === 'create') {
            const body = { apelido: form.apelido.trim() }
            body[this.parentIdField] = form.parentId
            await this.sdkNode.post(body)
          } else {
            await this.sdkNode._id.put({ id: editingId, apelido: form.apelido.trim() })
          }
          this.snackbar = { show: true, color: 'success', text: 'Salvo.' }
          this.dialog.show = false
          await this.reload()
        } catch (err) {
          console.error(`[${this.sdkPath}.submit]`, err)
          this.dialog.error = this.msgFromError(err, 'Falha ao salvar.')
        } finally {
          this.dialog.saving = false
        }
      },
      async remove (item) {
        if (!confirm(`Excluir apelido "${item.apelido}"?`)) return
        try {
          await this.sdkNode._id.delete({ id: item.id })
          this.snackbar = { show: true, color: 'success', text: 'Excluído.' }
          await this.reload()
        } catch (err) {
          console.error(`[${this.sdkPath}.delete]`, err)
          this.snackbar = { show: true, color: 'error', text: this.msgFromError(err, 'Falha ao excluir.') }
        }
      },
    },
  }
</script>
